import { useState, type FC } from 'react'
import { useTranslation } from 'react-i18next'
import type { Key } from 'react-aria-components'
import {
  Button as AriaButton,
  Autocomplete,
  Input,
  MenuItem,
  SearchField,
  useFilter,
} from 'react-aria-components'
import {
  DotsHorizontalIcon,
  EyeIcon,
  EyeOffIcon,
  SearchLgIcon,
  User01Icon,
} from '@untitledui-icons/react/line'
import { Button } from '@/components/base/buttons'
import { Dropdown } from '@/components/base/dropdown/dropdown'
import { Modal, ModalContent, ModalHeader, ModalBody } from '@/components/ui/Modal'
import { usePermissions } from '@/hooks/api'
import { useCreateNote } from '@/hooks/api/useConversationNotes'
import { cn } from '@/lib/utils'
import { toast } from '@/lib/toast'
import { MentionsTextArea } from '../components/ConversationNotes'
import {
  getMentionPayload,
  getTargetKey,
  getToastErrorMessage,
  type AssignmentTarget,
} from '../components/noteHelpers'
import { NotePriorityIcon } from './NotePriorityIcon'
import type { PriorityLevel } from '@/types/conversation'

const PRIORITIES: PriorityLevel[] = ['low', 'medium', 'high', 'critical']

function PriorityMenuIcon({ priority }: { priority: PriorityLevel }) {
  return (
    <span className="flex size-4 shrink-0 items-center justify-center text-gray-900 dark:text-gray-100">
      <span className={cn('flex', priority === 'critical' ? 'scale-115' : 'scale-125')}>
        <NotePriorityIcon priority={priority} />
      </span>
    </span>
  )
}

const priorityMenuIcons: Record<PriorityLevel, FC> = {
  low: () => <PriorityMenuIcon priority="low" />,
  medium: () => <PriorityMenuIcon priority="medium" />,
  high: () => <PriorityMenuIcon priority="high" />,
  critical: () => <PriorityMenuIcon priority="critical" />,
}

interface CreateNoteModalProps {
  isOpen: boolean
  onClose: () => void
  onCreated: () => void
  conversationId: string | number
  userTargets: AssignmentTarget[]
  teamTargets: AssignmentTarget[]
}

function PickerPill({
  icon,
  label,
  isSet,
  items,
  selectedKey,
  onSelect,
  searchPlaceholder,
}: {
  icon: React.ReactNode
  label: string
  isSet: boolean
  items: { id: string; label: string; icon?: FC }[]
  selectedKey: string | null
  onSelect: (key: Key) => void
  searchPlaceholder?: string
}) {
  const { contains } = useFilter({ sensitivity: 'base' })

  const menu = (
    <Dropdown.Menu
      items={items}
      selectedKeys={selectedKey ? [selectedKey] : []}
      onAction={onSelect}
      className="min-h-0 flex-1"
    >
      {(item) => {
        const Icon = item.icon
        return (
          <MenuItem
            id={item.id}
            textValue={item.label}
            className="flex cursor-pointer items-center gap-2 px-2.5 py-1.5 text-sm text-gray-900 outline-hidden data-[focused]:bg-gray-50 data-[hovered]:bg-gray-50 dark:text-gray-100 dark:data-[focused]:bg-gray-800 dark:data-[hovered]:bg-gray-800"
          >
            {Icon && <Icon />}
            <span className="truncate">{item.label}</span>
          </MenuItem>
        )
      }}
    </Dropdown.Menu>
  )

  return (
    <Dropdown.Root>
      <AriaButton
        className={cn(
          'inline-flex cursor-pointer items-center gap-1 rounded-full bg-gray-50 py-0.5 pr-2 pl-1.5 text-xs font-medium whitespace-nowrap text-gray-700 ring-1 ring-inset hover:bg-gray-100 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700',
          isSet ? 'ring-gray-900 dark:ring-gray-300' : 'ring-gray-200 dark:ring-gray-700'
        )}
      >
        {icon}
        {label}
      </AriaButton>
      <Dropdown.Popover
        placement="top start"
        className="flex max-h-72 w-53 flex-col overflow-hidden bg-white ring-gray-200 dark:bg-gray-900 dark:ring-gray-700"
      >
        {searchPlaceholder ? (
          <Autocomplete filter={contains}>
            <SearchField
              aria-label={searchPlaceholder}
              autoFocus
              className="flex shrink-0 items-center gap-2 border-b border-gray-200 px-2.5 py-2.5 dark:border-gray-700"
            >
              <SearchLgIcon
                className="size-4 shrink-0 text-gray-900 dark:text-gray-100"
                strokeWidth={2.5}
                aria-hidden="true"
              />
              <Input
                placeholder={searchPlaceholder}
                className="min-w-0 flex-1 bg-transparent text-sm text-gray-900 outline-hidden! placeholder:text-gray-400 dark:text-gray-100 [&::-webkit-search-cancel-button]:hidden"
              />
            </SearchField>
            {menu}
          </Autocomplete>
        ) : (
          menu
        )}
      </Dropdown.Popover>
    </Dropdown.Root>
  )
}

export function CreateNoteModal({
  isOpen,
  onClose,
  onCreated,
  conversationId,
  userTargets,
  teamTargets,
}: CreateNoteModalProps) {
  const { t } = useTranslation()
  const { canManage } = usePermissions()
  const createNote = useCreateNote()
  const [content, setContent] = useState('')
  const [mentionTargets, setMentionTargets] = useState<AssignmentTarget[]>([])
  const [assignee, setAssignee] = useState<AssignmentTarget | null>(null)
  const [priority, setPriority] = useState<PriorityLevel | null>(null)
  const [isPrivate, setIsPrivate] = useState(false)

  const assignmentTargets = [...userTargets, ...teamTargets]

  const reset = () => {
    setContent('')
    setMentionTargets([])
    setAssignee(null)
    setPriority(null)
    setIsPrivate(false)
  }

  const handleClose = () => {
    reset()
    onClose()
  }

  const handleCreate = () => {
    if (!content.trim()) return

    createNote.mutate(
      {
        conversation_id: conversationId,
        content,
        status: 'open',
        priority,
        is_private: canManage && isPrivate,
        assigned_to: assignee?.type === 'user' ? assignee.id : null,
        assigned_team_id: assignee?.type === 'team' ? assignee.id : null,
        ...getMentionPayload(content, mentionTargets),
      },
      {
        onSuccess: () => {
          toast.success(t('resources.conversation_notes.added'))
          reset()
          onCreated()
        },
        onError: (error: unknown) => {
          toast.error(getToastErrorMessage(error, t('resources.conversation_notes.add_failed')))
        },
      }
    )
  }

  return (
    <Modal isOpen={isOpen} onOpenChange={(open) => !open && handleClose()} size="2xl" isDismissable>
      <ModalContent>
        <ModalHeader onClose={handleClose} bordered={false} className="px-6 pt-6 pb-5">
          {t('resources.conversations.inbox.notes.create_title')}
        </ModalHeader>
        <ModalBody className="px-6 py-0">
          <MentionsTextArea
            value={content}
            onChange={setContent}
            placeholder={t('resources.conversation_notes.add_placeholder')}
            textAreaClassName="min-h-20"
            autoFocus
            userTargets={userTargets}
            teamTargets={teamTargets}
            selectedMentionTargets={mentionTargets}
            onSelectedMentionTargetsChange={setMentionTargets}
          />
        </ModalBody>
        <div className="flex items-center gap-3 px-6 pt-8 pb-6">
          <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
            <PickerPill
              icon={<User01Icon className="size-3" aria-hidden="true" />}
              label={assignee?.name ?? t('resources.conversations.inbox.notes.assignee')}
              isSet={!!assignee}
              items={assignmentTargets.map((target) => ({
                id: getTargetKey(target),
                label: target.name,
              }))}
              selectedKey={assignee ? getTargetKey(assignee) : null}
              searchPlaceholder={t('resources.conversation_notes.assign_search')}
              onSelect={(key) =>
                setAssignee(
                  assignmentTargets.find((target) => getTargetKey(target) === key) ?? null
                )
              }
            />
            <PickerPill
              icon={
                priority ? (
                  <NotePriorityIcon priority={priority} />
                ) : (
                  <DotsHorizontalIcon className="size-3" aria-hidden="true" />
                )
              }
              label={
                priority
                  ? t(`resources.conversation_notes.priority.${priority}`)
                  : t('resources.conversation_notes.priority.label')
              }
              isSet={!!priority}
              items={PRIORITIES.map((level) => ({
                id: level,
                label: t(`resources.conversation_notes.priority.${level}`),
                icon: priorityMenuIcons[level],
              }))}
              selectedKey={priority}
              searchPlaceholder={t('resources.conversation_notes.set_priority_to')}
              onSelect={(key) => setPriority(key as PriorityLevel)}
            />
            {canManage && (
              <button
                type="button"
                aria-pressed={isPrivate}
                onClick={() => setIsPrivate((current) => !current)}
                className={cn(
                  'inline-flex cursor-pointer items-center gap-1 rounded-full bg-gray-50 py-0.5 pr-2 pl-1.5 text-xs font-medium whitespace-nowrap text-gray-700 ring-1 ring-inset hover:bg-gray-100 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700',
                  isPrivate
                    ? 'ring-gray-900 dark:ring-gray-300'
                    : 'ring-gray-200 dark:ring-gray-700'
                )}
              >
                {isPrivate ? (
                  <EyeOffIcon className="size-3" aria-hidden="true" />
                ) : (
                  <EyeIcon className="size-3" aria-hidden="true" />
                )}
                {isPrivate
                  ? t('resources.conversation_notes.team_only')
                  : t('resources.conversation_notes.public')}
              </button>
            )}
          </div>
          <Button
            color="primary"
            size="md"
            onPress={handleCreate}
            isDisabled={!content.trim() || createNote.isPending}
            isLoading={createNote.isPending}
          >
            {t('resources.conversations.inbox.notes.create')}
          </Button>
        </div>
      </ModalContent>
    </Modal>
  )
}
